import { useState } from 'react';

import type { Carousel } from '@smartapply/shared';

import { CarouselPreview } from '../components/posts/CarouselPreview.js';

interface CarouselRouteProps {
  /** Base64 do JSON do carrossel, passado via query string ?d=... */
  encodedData: string;
}

interface LoadResult {
  carousel: Carousel | null;
  error: string | null;
}

/**
 * Decodifica o carrossel do parâmetro de URL base64. Suporta UTF-8 (acentos,
 * cedilha, etc) via TextDecoder. URL hash sobrevive a F5 e funciona cross-tab
 * sem depender de localStorage.
 */
function decodeCarousel(encoded: string): LoadResult {
  try {
    // base64 padrão pode ter +/= que conflitam com URL; usamos URL-safe base64.
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder('utf-8').decode(bytes);
    const parsed = JSON.parse(json) as Carousel;
    return { carousel: parsed, error: null };
  } catch {
    return {
      carousel: null,
      error: 'Carrossel inválido. Volte pra aba de drafts e gere novamente.',
    };
  }
}

/**
 * Página dedicada do carrossel. Aberta em nova aba via window.open() pelo
 * DraftCard com o JSON inteiro codificado em base64 na URL. Sobrevive a F5
 * e não depende de localStorage (cross-tab pode ter restrições).
 */
export function CarouselRoute({ encodedData }: CarouselRouteProps) {
  // Lazy init: decodifica só 1x na primeira render.
  const [{ carousel, error }] = useState<LoadResult>(() => decodeCarousel(encodedData));

  function handleClose() {
    // Tenta fechar a aba; se o browser bloquear (aba não-aberta-por-script),
    // limpa o sessionStorage e mostra mensagem.
    try {
      window.close();
    } catch {
      /* ignore */
    }
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--ink-primary)] p-8">
        <div className="max-w-md text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--bg-paper)]/60">
            Erro
          </p>
          <h1 className="mt-4 font-serif text-2xl text-[var(--bg-paper)]">{error}</h1>
          <button
            type="button"
            onClick={() => window.close()}
            className="mt-8 rounded-sm bg-[var(--accent-saffron)] px-5 py-3 font-sans text-sm font-medium text-[var(--ink-primary)] hover:opacity-90"
          >
            Fechar aba
          </button>
        </div>
      </div>
    );
  }

  if (!carousel) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--ink-primary)]">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--bg-paper)]/60">
          Carregando carrossel...
        </p>
      </div>
    );
  }

  return <CarouselPreview carousel={carousel} onClose={handleClose} />;
}
